import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Select,
  Skeleton,
  styles as ui,
  Tabs,
  Textarea,
  TextField,
  useConfirm,
  useToast,
} from "stories/Admin";
import {
  DUA_FIELD_LABELS,
  DuaAiDecisionResult,
  DuaAiRun,
  DuaAiSuggestion,
  RTL_FIELDS,
  sourceLabel,
} from "./duaAiTypes";
import styles from "./DuaPage.module.scss";

type Tab = "fixes" | "new" | "done";

const ACCEPT_CHUNK = 8;

interface DuaAiReviewDialogProps {
  run: DuaAiRun;
  onClose: () => void;
  onChanged: () => void;
  /** Kategoriene duaene har i dag (nedtrekk for nye duaer). */
  categories: string[];
}

/** Feltene en ny dua vises med (og kan redigeres i) før den godkjennes. */
const NEW_FIELDS: { field: string; rows?: number }[] = [
  { field: "tittel" },
  { field: "kategori" },
  { field: "arabisk", rows: 3 },
  { field: "oversettelse", rows: 3 },
  { field: "engelsk", rows: 3 },
  { field: "urdu", rows: 3 },
  { field: "transliteration", rows: 3 },
  { field: "referanse" },
  { field: "tags" },
];

/**
 * Gjennomgang av forslagene fra AI: rettelser kan godkjennes i bunke,
 * nye duaer gås gjennom (og kan redigeres) én og én.
 */
const DuaAiReviewDialog: React.FC<DuaAiReviewDialogProps> = ({
  run,
  onClose,
  onChanged,
  categories,
}) => {
  const titleId = useId();
  const { execute } = useFetch();
  const { execute: decide } = useFetch();
  const toast = useToast();
  const confirm = useConfirm();
  const [suggestions, setSuggestions] = useState<DuaAiSuggestion[] | null>(
    null,
  );
  const [tab, setTab] = useState<Tab>(
    run.pendingFixes > 0 || run.pendingNew === 0 ? "fixes" : "new",
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<number | "bulk" | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [edits, setEdits] = useState<Record<number, Record<string, string>>>(
    {},
  );

  const load = useCallback(async () => {
    const result = (await execute(
      `/api/v1/admin/dua-ai/runs/${run.id}/suggestions`,
      "GET",
    )) as DuaAiSuggestion[] | null;
    setSuggestions(result ?? []);
  }, [run.id]);

  // Først: lyd for hele koranpassasjer (alle versene slått sammen) på forslagene, så listen
  useEffect(() => {
    const prepare = async () => {
      if (run.status === "DONE") {
        await execute(
          `/api/v1/admin/dua-ai/runs/${run.id}/refresh-quran-audio`,
          "POST",
        );
      }
      await load();
    };
    prepare();
  }, [load, run.id, run.status]);

  const fixes = useMemo(
    () =>
      (suggestions ?? []).filter(
        (s) => s.kind === "FIX" && s.status === "PENDING",
      ),
    [suggestions],
  );
  const newOnes = useMemo(
    () =>
      (suggestions ?? []).filter(
        (s) => s.kind === "NEW" && s.status === "PENDING",
      ),
    [suggestions],
  );
  const decided = useMemo(
    () => (suggestions ?? []).filter((s) => s.status !== "PENDING"),
    [suggestions],
  );
  const safeFixes = fixes.filter((s) => s.warnings.length === 0);

  const report = (result: DuaAiDecisionResult | null, verb: string) => {
    if (!result) {
      toast.error("Noe gikk galt. Prøv igjen.");
      return;
    }
    const count = verb === "godkjent" ? result.accepted : result.rejected;
    if (result.failed.length > 0) {
      toast.error(
        `${count} ${verb}, ${result.failed.length} feilet: ${result.failed[0].error}`,
      );
    } else {
      toast.success(`${count} forslag ${verb}.`);
    }
    if (result.accepted > 0) onChanged();
  };

  /**
   * Godkjenner i bunker på 8, så hver forespørsel blir kort (lyd lastes ned og opp per dua)
   * og fremdriften kan vises. Edits gjelder bare når én dua godkjennes.
   */
  const acceptIds = async (
    ids: number[],
    withEdits?: Record<string, string>,
  ) => {
    const total: DuaAiDecisionResult = { accepted: 0, rejected: 0, failed: [] };
    let anyResponse = false;
    for (let i = 0; i < ids.length; i += ACCEPT_CHUNK) {
      const chunk = ids.slice(i, i + ACCEPT_CHUNK);
      if (ids.length > ACCEPT_CHUNK)
        setProgress({ done: i, total: ids.length });
      const result = (await decide(
        "/api/v1/admin/dua-ai/suggestions/accept",
        "POST",
        { ids: chunk, edits: withEdits },
      )) as DuaAiDecisionResult | null;
      if (result) {
        anyResponse = true;
        total.accepted += result.accepted;
        total.failed.push(...result.failed);
      } else {
        total.failed.push(
          ...chunk.map((id) => ({ id, error: "Fikk ikke svar fra serveren" })),
        );
      }
    }
    setProgress(null);
    report(anyResponse ? total : null, "godkjent");
  };

  const rejectIds = async (ids: number[]) => {
    const result = (await decide(
      "/api/v1/admin/dua-ai/suggestions/reject",
      "POST",
      { ids },
    )) as DuaAiDecisionResult | null;
    report(result, "avvist");
  };

  const runAction = async (
    key: number | "bulk",
    action: () => Promise<void>,
  ) => {
    setBusy(key);
    await action();
    setBusy(null);
    setSelected(new Set());
    await load();
  };

  const acceptAllSafe = async () => {
    const ok = await confirm({
      title: `Godkjenne ${safeFixes.length} rettelser?`,
      message:
        "Alle rettelser uten advarsel lagres på duaene, og lydfiler som er funnet lastes opp. Rettelser med advarsel må godkjennes enkeltvis.",
      confirmLabel: "Godkjenn alle",
    });
    if (!ok) return;
    await runAction("bulk", () => acceptIds(safeFixes.map((f) => f.id)));
  };

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const editValue = (s: DuaAiSuggestion, field: string) =>
    edits[s.id]?.[field] ?? s.proposed[field] ?? "";

  const setEdit = (id: number, field: string, value: string) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const listed = tab === "fixes" ? fixes : tab === "new" ? newOnes : decided;
  const selectedInTab = listed
    .filter((s) => selected.has(s.id))
    .map((s) => s.id);

  return createPortal(
    <div
      className={ui.overlay}
      style={{ paddingTop: 40 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && busy === null) onClose();
      }}
    >
      <div
        className={`${ui.modal} ${styles.reviewModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape" && busy === null) {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 className={ui.mh} id={titleId}>
          <span className={ui.mhIcon} aria-hidden="true">
            <Icon name="spark" size={18} strokeWidth={2} />
          </span>
          Forslag fra AI-gjennomgangen
          <IconButton
            label="Lukk"
            icon={<Icon name="x" size={18} strokeWidth={2} />}
            onClick={onClose}
            disabled={busy !== null}
            style={{ marginLeft: "auto" }}
          />
        </h2>
        <div className={`${ui.mb} ${styles.reviewBody}`}>
          <Tabs<Tab>
            label="Forslag"
            value={tab}
            onChange={(t) => {
              setTab(t);
              setSelected(new Set());
            }}
            tabs={[
              { value: "fixes", label: `Rettelser (${fixes.length})` },
              { value: "new", label: `Nye duaer (${newOnes.length})` },
              { value: "done", label: `Behandlet (${decided.length})` },
            ]}
          />

          {tab === "fixes" && fixes.length > 0 && (
            <div className={styles.reviewToolbar}>
              <span className={styles.aiSub}>
                Rettelser uten advarsel kan godkjennes samlet. Merk av for å
                godkjenne eller avvise flere.
              </span>
              <Button
                size="sm"
                onClick={acceptAllSafe}
                loading={busy === "bulk"}
                disabled={busy !== null || safeFixes.length === 0}
              >
                {progress
                  ? `Godkjenner ${progress.done} av ${progress.total} …`
                  : `Godkjenn alle uten advarsel (${safeFixes.length})`}
              </Button>
            </div>
          )}
          {tab === "new" && newOnes.length > 0 && (
            <Alert tone="info">
              Nye duaer må gås gjennom én og én. Du kan rette tekstene før du
              godkjenner. Lydfilen lastes opp til iBønn når duaen godkjennes.
            </Alert>
          )}
          {selectedInTab.length > 0 && tab !== "done" && (
            <div className={styles.reviewToolbar}>
              <span className={styles.aiSub}>{selectedInTab.length} valgt</span>
              <div className={styles.reviewButtons}>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy !== null}
                  onClick={() =>
                    runAction("bulk", () => rejectIds(selectedInTab))
                  }
                >
                  Avvis valgte
                </Button>
                {tab === "fixes" && (
                  <Button
                    size="sm"
                    disabled={busy !== null}
                    loading={busy === "bulk"}
                    onClick={() =>
                      runAction("bulk", () => acceptIds(selectedInTab))
                    }
                  >
                    Godkjenn valgte
                  </Button>
                )}
              </div>
            </div>
          )}

          {suggestions === null ? (
            <div className={styles.reviewList}>
              <Skeleton height={120} />
              <Skeleton height={120} />
            </div>
          ) : listed.length === 0 ? (
            <EmptyState icon="check" title="Ingenting her">
              {tab === "done"
                ? "Ingen forslag er behandlet ennå."
                : "Alle forslagene i denne fanen er behandlet."}
            </EmptyState>
          ) : (
            <div className={styles.reviewList}>
              {listed.map((s) => (
                <div key={s.id} className={styles.reviewItem}>
                  <div className={styles.itemHead}>
                    {s.status === "PENDING" && (
                      <input
                        type="checkbox"
                        aria-label="Velg forslaget"
                        checked={selected.has(s.id)}
                        onChange={() => toggle(s.id)}
                      />
                    )}
                    <b className={styles.itemTitle}>
                      {s.title ?? "Uten tittel"}
                      {s.duaId != null && (
                        <span className={styles.aiSub}> · dua #{s.duaId}</span>
                      )}
                    </b>
                    {sourceLabel(s.sourceRef) && (
                      <Badge tone="lilac">{sourceLabel(s.sourceRef)}</Badge>
                    )}
                    {s.kind === "NEW" && <Badge tone="mint">Ny</Badge>}
                    {s.status === "ACCEPTED" && (
                      <Badge tone="mint">Godkjent</Badge>
                    )}
                    {s.status === "REJECTED" && (
                      <Badge tone="grey">Avvist</Badge>
                    )}
                  </div>
                  {s.warnings.map((w, i) => (
                    <Alert key={i} tone="warn">
                      {w}
                    </Alert>
                  ))}
                  {s.error && s.status === "PENDING" && (
                    <Alert tone="error">Forrige forsøk feilet: {s.error}</Alert>
                  )}
                  {s.status !== "PENDING" && s.decidedBy && (
                    <span className={styles.aiSub}>
                      {s.status === "ACCEPTED" ? "Godkjent" : "Avvist"} av{" "}
                      {s.decidedBy}
                    </span>
                  )}

                  {s.kind === "FIX" &&
                    s.changes.map((c) => (
                      <div key={c.field} className={styles.change}>
                        <div className={styles.changeHead}>
                          <b>{c.label}</b>
                          {c.reason && <span>{c.reason}</span>}
                        </div>
                        {c.field === "lyd" ? (
                          c.after && (
                            <audio
                              controls
                              preload="none"
                              src={c.after}
                              className={styles.audio}
                            />
                          )
                        ) : (
                          <div className={styles.diff}>
                            {c.before && (
                              <p
                                className={styles.before}
                                dir={
                                  RTL_FIELDS.has(c.field) ? "rtl" : undefined
                                }
                                lang={
                                  RTL_FIELDS.has(c.field)
                                    ? c.field === "urdu"
                                      ? "ur"
                                      : "ar"
                                    : undefined
                                }
                              >
                                {c.before}
                              </p>
                            )}
                            <p
                              className={styles.after}
                              dir={RTL_FIELDS.has(c.field) ? "rtl" : undefined}
                              lang={
                                RTL_FIELDS.has(c.field)
                                  ? c.field === "urdu"
                                    ? "ur"
                                    : "ar"
                                  : undefined
                              }
                            >
                              {c.after}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}

                  {s.kind === "NEW" && s.status === "PENDING" && (
                    <div className={styles.newFields}>
                      {NEW_FIELDS.map(({ field, rows }) =>
                        rows ? (
                          <Textarea
                            key={field}
                            label={DUA_FIELD_LABELS[field]}
                            rows={rows}
                            dir={RTL_FIELDS.has(field) ? "rtl" : undefined}
                            className={
                              field === "arabisk"
                                ? styles.arabicInput
                                : undefined
                            }
                            value={editValue(s, field)}
                            onChange={(e) =>
                              setEdit(s.id, field, e.target.value)
                            }
                          />
                        ) : field === "kategori" ? (
                          <Select
                            key={field}
                            label="Kategori (AI-forslag)"
                            value={editValue(s, field)}
                            onChange={(e) =>
                              setEdit(s.id, field, e.target.value)
                            }
                          >
                            {Array.from(
                              new Set([...categories, editValue(s, field)]),
                            )
                              .filter(Boolean)
                              .sort((a, b) => a.localeCompare(b, "nb"))
                              .map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                          </Select>
                        ) : (
                          <TextField
                            key={field}
                            label={DUA_FIELD_LABELS[field]}
                            value={editValue(s, field)}
                            onChange={(e) =>
                              setEdit(s.id, field, e.target.value)
                            }
                          />
                        ),
                      )}
                      {s.audioSourceUrl && (
                        <audio
                          controls
                          preload="none"
                          src={s.audioSourceUrl}
                          className={styles.audio}
                        />
                      )}
                    </div>
                  )}
                  {s.kind === "NEW" && s.status !== "PENDING" && (
                    <p className={styles.translation}>
                      {s.proposed.oversettelse}
                    </p>
                  )}

                  {s.status === "PENDING" && (
                    <div className={styles.reviewButtons}>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy !== null}
                        onClick={() => runAction(s.id, () => rejectIds([s.id]))}
                      >
                        Avvis
                      </Button>
                      <Button
                        size="sm"
                        loading={busy === s.id}
                        disabled={busy !== null}
                        onClick={() =>
                          runAction(s.id, () => acceptIds([s.id], edits[s.id]))
                        }
                      >
                        {s.kind === "NEW" ? "Legg til duaen" : "Godkjenn"}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DuaAiReviewDialog;
